import type { Dispatch, SetStateAction } from 'react';
import {
  RefreshCw, ChevronDown, ChevronUp, User, Package,
  Wrench, Search, MessageCircle, Star, ShieldCheck, ShieldOff, Factory, Kanban,
} from 'lucide-react';
import type { AdminOrderActionMessage } from '@/hooks/admin/useAdminOrderActions';
import type { AdminMeasurement } from './AdminMeasurementsPanel';
import { AdminGlassDisclosure } from '@/components/admin/AdminGlassDisclosure';
import { AdminActionBanner } from '@/components/admin/AdminActionBanner';
import { openTelegramUserDm } from '@/openTelegramUser';
import { AdminMeasurementsPanel } from './AdminMeasurementsPanel';
import { CrmOrderListStrip } from '@/components/admin/crm/CrmOrderListStrip';
import {
  STATUSES,
  ORDER_NOTE_TEMPLATES,
  statusMeta,
  mergeOrderItemSnapshot,
  resolveOrderItemTitle,
  resolveOrderItemDetailLines,
  readSnapshotBreakdown,
  managerDisplayName,
  extractMeasurementSourceLabel,
  INTEREST_LABELS,
  MEASUREMENT_STATUS_META,
  type AdminOrder,
  type ItemDraft,
  type NewItemDraft,
  type OrderScope,
} from './adminOrdersShared';
import type { AdminManagerBrief, AdminOrderCrmStats } from '@/api';
import { CrmOrderStatusPanel } from '@/components/admin/crm/CrmOrderStatusPanel';

export type AdminOrdersPanelProps = {
  canManageStaff: boolean;
  fullAdmin: boolean;
  crmUi: boolean;
  isProductionSandbox: boolean;
  isSuperAdmin: boolean;
  orderStatusEditor: boolean;
  pendingMeasurementCount: number;
  pendingQueueCount: number;
  showMeasurementsPanel: boolean;
  setShowMeasurementsPanel: (v: boolean) => void;
  filterStatus: string;
  setFilterStatus: (v: string) => void;
  orderScope: OrderScope;
  setOrderScope: (v: OrderScope) => void;
  orderSearch: string;
  setOrderSearch: (v: string) => void;
  ordersManagerFilterId: number | null;
  setOrdersManagerFilterId: (id: number | null) => void;
  crmStats: AdminOrderCrmStats | null;
  orders: AdminOrder[];
  ordersLoading: boolean;
  ordersError: string | null;
  expanded: number | null;
  setExpanded: (id: number | null) => void;
  updating: number | null;
  haptic: { selection: () => void; impact: (s: 'light' | 'medium' | 'heavy') => void };
  setTab: (tab: 'dashboard') => void;
  openDealCard: (orderId: number) => void;
  claimOrder: (orderId: number) => Promise<void>;
  actionMessage: AdminOrderActionMessage | null;
  canPurgeOrder: boolean;
  managers: AdminManagerBrief[];
  assignOrderManager: (orderId: number, managerId: number | null) => Promise<void>;
  measurements: AdminMeasurement[];
  orderMeasOpen: Record<number, boolean>;
  setOrderMeasOpen: React.Dispatch<React.SetStateAction<Record<number, boolean>>>;
  orderComposeEditorOpen: Record<number, boolean>;
  setOrderComposeEditorOpen: React.Dispatch<React.SetStateAction<Record<number, boolean>>>;
  setPurgeConfirmOrder: (order: AdminOrder | null) => void;
  notes: Record<number, string>;
  setNotes: Dispatch<SetStateAction<Record<number, string>>>;
  orderDiscounts: Record<number, number>;
  setOrderDiscounts: Dispatch<SetStateAction<Record<number, number>>>;
  itemDrafts: Record<number, ItemDraft>;
  setItemDrafts: Dispatch<SetStateAction<Record<number, ItemDraft>>>;
  newItemDrafts: Record<number, NewItemDraft>;
  setNewItemDrafts: Dispatch<SetStateAction<Record<number, NewItemDraft>>>;
  saveManagerNote: (orderId: number, status: string) => void;
  updateOrderPricing: (orderId: number) => void;
  onOrderStatusUpdated: () => void;
  addOrderItem: (orderId: number) => void;
  updateOrderItem: (orderId: number, itemId: number) => void;
  deleteOrderItem: (orderId: number, itemId: number) => void;
  measurementsPanelProps: React.ComponentProps<typeof AdminMeasurementsPanel>;
};

export function AdminOrdersPanel(props: AdminOrdersPanelProps) {
  const {
    canManageStaff,
    fullAdmin,
    crmUi,
    isProductionSandbox,
    isSuperAdmin,
    orderStatusEditor,
    pendingMeasurementCount,
    pendingQueueCount,
    showMeasurementsPanel,
    setShowMeasurementsPanel,
    filterStatus,
    setFilterStatus,
    orderScope,
    setOrderScope,
    orderSearch,
    setOrderSearch,
    ordersManagerFilterId,
    setOrdersManagerFilterId,
    crmStats,
    orders,
    ordersLoading,
    ordersError,
    expanded,
    setExpanded,
    updating,
    haptic,
    setTab,
    openDealCard,
    claimOrder,
    actionMessage,
    canPurgeOrder,
    managers,
    assignOrderManager,
    measurements,
    orderMeasOpen,
    setOrderMeasOpen,
    setPurgeConfirmOrder,
    notes,
    setNotes,
    orderDiscounts,
    setOrderDiscounts,
    saveManagerNote,
    updateOrderPricing,
    onOrderStatusUpdated,
    measurementsPanelProps,
  } = props;

  const useCrmOrderStrips = crmUi && canManageStaff;

  return (
    <>
                {canManageStaff && pendingMeasurementCount > 0 && !showMeasurementsPanel && (
            <div className="px-4 pt-3">
              <div
                className="rounded-2xl px-3 py-2.5 flex items-center justify-between gap-3"
                style={{ background: 'rgba(72,187,120,0.12)', border: '1px solid rgba(72,187,120,0.4)' }}
              >
                <div className="min-w-0">
                  <p className="text-xs font-bold" style={{ color: '#9AE6B4' }}>
                    {pendingMeasurementCount} заявок на замер ждут обработки
                  </p>
                  <p className="text-[10px] mt-0.5" style={{ color: 'var(--text-3)' }}>
                    Новые заявки из бота и Mini App — назначьте замерщика и статус.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    haptic.selection();
                    setShowMeasurementsPanel(true);
                  }}
                  className="press shrink-0 px-3 py-2 rounded-xl text-[11px] font-bold"
                  style={{ background: 'rgba(72,187,120,0.25)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)' }}
                >
                  Замеры
                </button>
              </div>
            </div>
          )}
          {pendingQueueCount > 0 && filterStatus !== 'pending' && (
            <div className="px-4 pt-3">
              <div
                className="rounded-2xl px-3 py-2.5 flex items-center justify-between gap-3"
                style={{ background: 'rgba(252,129,74,0.12)', border: '1px solid rgba(252,129,74,0.4)' }}
              >
                <div className="min-w-0">
                  <p className="text-xs font-bold" style={{ color: '#FBD38D' }}>
                    {pendingQueueCount} заказ(ов) ждут подтверждения
                  </p>
                  <p className="text-[10px] mt-0.5" style={{ color: 'var(--text-3)' }}>
                    Сначала обработайте очередь «Ожидает» — они подняты вверху списка при фильтре «Все».
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    haptic.selection();
                    setFilterStatus('pending');
                  }}
                  className="press shrink-0 px-3 py-2 rounded-xl text-[11px] font-bold"
                  style={{ background: 'rgba(252,129,74,0.25)', color: '#fff', border: '1px solid rgba(255,255,255,0.2)' }}
                >
                  Показать
                </button>
              </div>
            </div>
          )}
          {/* Status filter */}
          <div className="px-4 py-2" style={{ borderBottom: '1px solid var(--border-subtle)' }}>
{canManageStaff && (
              <AdminMeasurementsPanel {...measurementsPanelProps} />
            )}
            <div className="flex gap-1.5 overflow-x-auto pb-2 no-scrollbar mb-1">
              {([
                ['all', 'Все заказы'],
                ['mine', 'Мои'],
                ['unassigned', `Без менеджера${crmStats ? ` (${crmStats.unassigned_orders})` : ''}`],
              ] as const).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => { haptic.selection(); setOrderScope(key); }}
                  className="press shrink-0 px-3 py-1.5 rounded-full text-[10px] font-semibold"
                  style={orderScope === key
                    ? { background: 'rgba(99,179,237,0.2)', color: 'var(--cyan-bright)', border: '1px solid var(--cyan-border)' }
                    : { background: 'rgba(255,255,255,0.06)', color: 'var(--text-2)', border: '1px solid var(--border-subtle)' }}
                >
                  {label}
                </button>
              ))}
            </div>
            {!fullAdmin && crmStats && (
              <p className="text-[10px] mb-2 px-1" style={{ color: 'var(--text-3)' }}>
                Статистика и «Мои» — ваши заказы · активных {crmStats.active_orders} · выручка за месяц {Math.round(crmStats.revenue_month).toLocaleString('ru-RU')} ₽
              </p>
            )}
            {ordersManagerFilterId != null && (
              <p className="text-[10px] mb-2 px-1 flex items-center justify-between gap-2" style={{ color: 'var(--cyan)' }}>
                <span>Фильтр: заказы менеджера #{ordersManagerFilterId}</span>
                <button
                  type="button"
                  className="press px-2 py-0.5 rounded-lg text-[10px] font-semibold"
                  style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid var(--border-glass)' }}
                  onClick={() => { setOrdersManagerFilterId(null); haptic.selection(); }}
                >
                  Сбросить
                </button>
              </p>
            )}
            <div className="relative mb-2">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-3)' }} />
              <input
                type="text"
                placeholder="Поиск: номер, имя, username, Telegram ID..."
                value={orderSearch}
                onChange={(e) => setOrderSearch(e.target.value)}
                className="input-glass w-full pl-9 pr-3 py-2 rounded-xl text-xs"
              />
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1 no-scrollbar">
              <button
                onClick={() => setFilterStatus('')}
                className="press shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold"
                style={filterStatus === ''
                  ? { background: 'rgba(99,179,237,0.2)', color: 'var(--cyan-bright)', border: '1px solid var(--cyan-border)' }
                  : { background: 'rgba(255,255,255,0.06)', color: 'var(--text-2)', border: '1px solid var(--border-subtle)' }
                }
              >
                Активные ({orders.length})
              </button>
              {STATUSES.map(s => (
                <button
                  key={s.value}
                  onClick={() => { haptic.selection(); setFilterStatus(filterStatus === s.value ? '' : s.value); }}
                  className="press shrink-0 px-3 py-1.5 rounded-full text-xs font-semibold inline-flex items-center gap-1.5"
                  style={filterStatus === s.value
                    ? { background: s.glassColor, color: s.textColor, border: `1px solid ${s.borderColor}` }
                    : { background: 'rgba(255,255,255,0.06)', color: 'var(--text-2)', border: '1px solid var(--border-subtle)' }
                  }
                >
                  {s.label}
                  {s.value === 'pending' && pendingQueueCount > 0 && (
                    <span
                      className="text-[10px] font-black px-1.5 py-0.5 rounded-full"
                      style={{ background: 'rgba(252,129,74,0.35)', color: '#FBD38D' }}
                    >
                      {pendingQueueCount > 99 ? '99+' : pendingQueueCount}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          <div className="p-4 space-y-3">
            {useCrmOrderStrips && (
              <p
                className="text-[11px] leading-relaxed rounded-xl px-3 py-2"
                style={{ color: 'var(--text-3)', background: 'rgba(99,179,237,0.06)', border: '1px solid var(--border-glass)' }}
              >
                Компактный список: состав, замеры и задачи — только в{' '}
                <strong style={{ color: 'var(--cyan-bright)' }}>карточке сделки</strong> (воронка и кнопка ниже).
              </p>
            )}
            {ordersError && (
              <div className="rounded-xl px-3 py-2 text-xs" style={{ background: 'rgba(252,129,74,0.12)', color: '#FC814A', border: '1px solid rgba(252,129,74,0.35)' }}>
                {ordersError}
              </div>
            )}
            {ordersLoading && (
              <div className="flex justify-center py-12">
                <RefreshCw size={24} className="animate-spin" style={{ color: 'var(--cyan)' }} />
              </div>
            )}
            {!ordersLoading && orders.length === 0 && (
              <div
                className="rounded-2xl p-4 text-center space-y-3"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid var(--border-subtle)' }}
              >
                <Package size={40} className="mx-auto opacity-30" style={{ color: 'var(--text-3)' }} />
                {orderSearch.trim() || filterStatus || orderScope !== 'all' || ordersManagerFilterId != null ? (
                  <>
                    <p className="text-sm font-bold" style={{ color: 'var(--text-1)' }}>Нет заказов по фильтру</p>
                    <p className="text-xs" style={{ color: 'var(--text-3)' }}>
                      Сбросьте фильтры — возможно, заказы есть, но скрыты областью «{orderScope === 'mine' ? 'мои' : orderScope === 'unassigned' ? 'без менеджера' : 'все'}».
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        haptic.selection();
                        setFilterStatus('');
                        setOrderSearch('');
                        setOrderScope('all');
                        setOrdersManagerFilterId(null);
                      }}
                      className="press px-4 py-2.5 rounded-xl text-xs font-bold touch-manipulation min-h-[44px]"
                      style={{ background: 'rgba(99,179,237,0.15)', color: '#63B3ED', border: '1px solid rgba(99,179,237,0.35)' }}
                    >
                      Показать все заказы
                    </button>
                  </>
                ) : (
                  <>
                    <p className="text-sm font-bold" style={{ color: 'var(--text-1)' }}>В базе нет заказов</p>
                    <p className="text-xs leading-relaxed" style={{ color: 'var(--text-3)' }}>
                      Это не сброс CRM: либо новая пустая БД на Railway, либо другой DATABASE_URL. Старые заказы сами не вернутся — только бэкап Postgres или новые оформления из Mini App.
                    </p>
                    {isSuperAdmin && (
                      <button
                        type="button"
                        onClick={() => {
                          haptic.selection();
                          setTab('dashboard');
                        }}
                        className="press px-4 py-2.5 rounded-xl text-xs font-bold touch-manipulation min-h-[44px]"
                        style={{ background: 'rgba(159,122,234,0.15)', color: '#D6BCFA', border: '1px solid rgba(159,122,234,0.35)' }}
                      >
                        Состояние базы на дашборде
                      </button>
                    )}
                  </>
                )}
              </div>
            )}
            {!ordersLoading && orders.map(order => {
              const meta = statusMeta(order.status);
              const isExpanded = expanded === order.id;
              const Icon = meta.icon;
              const isPendingQueue = order.status === 'pending';

              if (useCrmOrderStrips) {
                return (
                  <CrmOrderListStrip
                    key={order.id}
                    order={order}
                    updating={updating === order.id}
                    hapticSelection={() => haptic.selection()}
                    onOpenDeal={() => openDealCard(order.id)}
                    onQuickAction={
                      !order.assigned_manager_id
                        ? () => void claimOrder(order.id)
                        : () => {
                            haptic.selection();
                            openDealCard(order.id);
                          }
                    }
                    quickActionLabel={order.assigned_manager_id ? 'В CRM' : undefined}
                  />
                );
              }

              /* Legacy expandable rows — only when !useCrmOrderStrips (staff without CRM UI) */
              return (
                <div
                  key={order.id}
                  className="glass rounded-2xl overflow-hidden"
                  style={
                    isPendingQueue
                      ? { boxShadow: '0 0 0 2px rgba(252,129,74,0.65), 0 8px 28px rgba(252,129,74,0.12)' }
                      : undefined
                  }
                >
                  <button className="press w-full px-4 py-3 flex items-center gap-3 text-left" onClick={() => { haptic.selection(); setExpanded(isExpanded ? null : order.id); }}>
                    <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
                      style={{ background: meta.glassColor, border: `1px solid ${meta.borderColor}` }}>
                      <Icon size={16} style={{ color: meta.textColor }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-bold text-sm" style={{ color: 'var(--text-1)' }}>{order.order_number}</span>
                        <span className="font-bold text-sm" style={{ color: 'var(--cyan-bright)' }}>{order.final_amount.toLocaleString('ru-RU')} ₽</span>
                      </div>
                      <div className="flex items-center justify-between mt-0.5">
                        <span className="text-xs flex items-center gap-1" style={{ color: 'var(--text-3)' }}>
                          <User size={10} /> {order.user.first_name || order.user.username || `TG ${order.user.telegram_id}`}
                          {order.user.role === 'partner' && (
                            <span className="ml-1 text-[10px] font-bold px-1.5 rounded-full"
                              style={{ background: 'rgba(99,179,237,0.15)', color: 'var(--cyan)' }}>Партнёр</span>
                          )}
                        </span>
                        <span className="text-xs" style={{ color: 'var(--text-3)' }}>{new Date(order.created_at).toLocaleDateString('ru-RU')}</span>
                      </div>
                      {order.updated_at && (
                        <p className="text-[10px] mt-0.5" style={{ color: 'var(--text-3)' }}>
                          обновлено {new Date(order.updated_at).toLocaleDateString('ru-RU')} · {new Date(order.updated_at).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
                        </p>
                      )}
                    </div>
                    {isExpanded
                      ? <ChevronUp size={16} style={{ color: 'var(--text-3)' }} className="shrink-0" />
                      : <ChevronDown size={16} style={{ color: 'var(--text-3)' }} className="shrink-0" />}
                  </button>

                  {isExpanded && (
                    <div className="px-4 pb-4 pt-3 space-y-4" style={{ borderTop: '1px solid var(--border-subtle)' }}>
                      <button
                        type="button"
                        onClick={() => {
                          haptic.selection();
                          openTelegramUserDm(order.user.telegram_id, order.user.username);
                        }}
                        className="flex w-full items-center gap-2 text-xs font-semibold px-3 py-2 rounded-xl press touch-manipulation min-h-[44px]"
                        style={{ background: 'rgba(99,179,237,0.1)', color: 'var(--cyan)', border: '1px solid var(--border-glass)' }}
                      >
                        <MessageCircle size={13} /> Написать клиенту в Telegram
                      </button>

                      {crmUi && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            openDealCard(order.id);
                          }}
                          className="press w-full flex items-center justify-center gap-2 text-xs font-bold px-3 py-2.5 rounded-xl touch-manipulation min-h-[48px]"
                          style={{ background: 'rgba(99,179,237,0.2)', color: '#63B3ED', border: '1px solid rgba(99,179,237,0.35)' }}
                        >
                          <Kanban size={14} /> Карточка сделки
                        </button>
                      )}

                      {crmUi && !isProductionSandbox && (
                        <p className="text-[10px] px-1 leading-relaxed" style={{ color: 'var(--text-3)' }}>
                          Задачи, этап воронки, состав и лента — в «Карточке сделки» (та же, что во вкладке Воронка).
                        </p>
                      )}

                      {canManageStaff && !order.assigned_manager_id && (
                        <div className="space-y-1.5">
                          <button
                            type="button"
                            disabled={updating === order.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              void claimOrder(order.id);
                            }}
                            className="flex w-full items-center justify-center gap-2 text-xs font-bold px-3 py-2.5 rounded-xl press disabled:opacity-50 touch-manipulation min-h-[48px]"
                            style={{ background: 'rgba(246,173,85,0.18)', color: '#F6AD55', border: '1px solid rgba(246,173,85,0.4)' }}
                          >
                            <ShieldCheck size={14} /> {updating === order.id ? 'Назначаем…' : 'Взять заказ в работу'}
                          </button>
                          {actionMessage && actionMessage.orderId === order.id && (
                            <AdminActionBanner text={actionMessage.text} tone={actionMessage.tone} />
                          )}
                        </div>
                      )}
                      {order.assigned_manager && (
                        <p className="text-[10px] px-2" style={{ color: 'var(--text-3)' }}>
                          Менеджер: <span style={{ color: 'var(--cyan)' }}>{managerDisplayName(order.assigned_manager) || `#${order.assigned_manager.id}`}</span>
                        </p>
                      )}
                      {!order.assigned_manager_id && order.client_assigned_manager_id && (
                        <p className="text-[10px] px-2" style={{ color: 'var(--text-3)' }}>
                          Постоянный менеджер клиента: #{order.client_assigned_manager_id}
                        </p>
                      )}
                      {canPurgeOrder && (
                        <button
                          type="button"
                          disabled={updating === order.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            setPurgeConfirmOrder(order);
                          }}
                          className="press w-full flex items-center justify-center gap-2 py-3 rounded-xl text-xs font-bold touch-manipulation min-h-[44px]"
                          style={{
                            background: 'rgba(252,129,74,0.12)',
                            color: '#FC814A',
                            border: '1px solid rgba(252,129,74,0.45)',
                          }}
                        >
                          <ShieldOff size={14} />
                          {updating === order.id ? '…' : 'Удалить заказ навсегда (только platform owner)'}
                        </button>
                      )}

                      {fullAdmin && managers.length > 0 && (
                        <div className="space-y-1">
                          <p className="text-[10px] font-bold uppercase tracking-widest px-1" style={{ color: 'var(--cyan)' }}>
                            Менеджер на заказе
                          </p>
                          <select
                            className="input-glass w-full rounded-xl text-xs py-2"
                            value={order.assigned_manager_id ?? ''}
                            disabled={updating === order.id}
                            onChange={(e) => {
                              const v = e.target.value;
                              void assignOrderManager(order.id, v === '' ? null : Number(v));
                            }}
                          >
                            <option value="">Без менеджера</option>
                            {managers.map((m) => (
                              <option key={m.id} value={m.id}>
                                {managerDisplayName(m) || `Менеджер #${m.id}`}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      {fullAdmin && (
                      <AdminGlassDisclosure
                        accent="emerald"
                        density="compact"
                        open={!!orderMeasOpen[order.id]}
                        onOpenChange={(o) => setOrderMeasOpen((prev) => ({ ...prev, [order.id]: o }))}
                        title="Замеры этого клиента"
                        subtitle="Заявки, привязанные к TG этого пользователя"
                        icon={<Wrench size={15} color="#68D391" />}
                        badge={measurements.filter((mm) => mm.user.id === order.user.id).length}
                      >
                            {measurements.filter((mm) => mm.user.id === order.user.id).length === 0 ? (
                              <p className="text-xs leading-relaxed" style={{ color: 'var(--text-3)' }}>
                                Нет записей замера для этого аккаунта (<code style={{ fontSize: '10px' }}>user #{order.user.id}</code>). Заявки из чата привязаны к тому Telegram-профилю, с которого написано в бот.
                              </p>
                            ) : (
                              <div className="space-y-2 max-h-48 overflow-y-auto">
                                {measurements.filter((mm) => mm.user.id === order.user.id).slice(0, 12).map((mm) => {
                                  const msm = MEASUREMENT_STATUS_META[mm.status] || {
                                    label: mm.status,
                                    color: 'var(--text-2)',
                                    bg: 'rgba(255,255,255,0.08)',
                                    border: 'rgba(255,255,255,0.18)',
                                  };
                                  const src = extractMeasurementSourceLabel(mm.notes);
                                  return (
                                    <div
                                      key={mm.id}
                                      className="rounded-lg px-2.5 py-2 text-[11px]"
                                      style={{ background: 'rgba(99,179,237,0.06)', border: '1px solid var(--border-glass)' }}
                                    >
                                      <div className="flex items-start justify-between gap-2 mb-1">
                                        <span className="font-bold" style={{ color: 'var(--text-1)' }}>#{mm.id}</span>
                                        <span
                                          className="text-[9px] font-bold px-2 py-0.5 rounded-full shrink-0"
                                          style={{ background: msm.bg, color: msm.color, border: `1px solid ${msm.border}` }}
                                        >
                                          {msm.label}
                                        </span>
                                      </div>
                                      <p className="truncate" style={{ color: 'var(--text-2)' }}>{mm.city || '—'} · {mm.phone || '—'}</p>
                                      <p style={{ color: 'var(--text-3)' }}>{mm.preferred_time || 'Время не указано'}</p>
                                      {(mm.interest || src) && (
                                        <p className="truncate mt-1" style={{ color: 'var(--cyan)' }}>
                                          {INTEREST_LABELS[mm.interest || ''] || mm.interest}
                                          {src ? ` · ${src}` : ''}
                                        </p>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                      </AdminGlassDisclosure>
                      )}

                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-widest mb-2" style={{ color: 'var(--cyan)' }}>◈ Состав</p>
                        <div className="space-y-1.5">
                          {order.items.map(item => {
                            const snap = mergeOrderItemSnapshot(
                              item.product_snapshot as Record<string, unknown>,
                              item.custom_params as Record<string, unknown> | undefined,
                              { length_m: item.length_m, width_m: item.width_m },
                            );
                            const name = resolveOrderItemTitle(snap);
                            const cfgLines = resolveOrderItemDetailLines(snap);
                            const breakdown = readSnapshotBreakdown(snap);
                            return (
                              <div key={item.id} className="flex items-start justify-between text-sm gap-2">
                                <div className="flex-1 min-w-0">
                                  <span className="leading-tight" style={{ color: 'var(--text-2)' }}>{name}</span>
                                  {cfgLines.map((line) => (
                                    <p key={`${item.id}-${line}`} className="text-[11px] mt-0.5" style={{ color: 'var(--cyan)' }}>{line}</p>
                                  ))}
                                  {(breakdown.base !== null || breakdown.surcharges.length > 0) && (
                                    <div className="mt-1 text-[11px]">
                                      {breakdown.base !== null && (
                                        <p style={{ color: 'var(--text-3)' }}>База: {breakdown.base.toLocaleString('ru-RU')} ₽</p>
                                      )}
                                      {breakdown.surcharges.map((s) => (
                                        <p key={`${item.id}-${s.label}`} style={{ color: 'var(--text-3)' }}>
                                          + {s.label}: {s.amount.toLocaleString('ru-RU')} ₽
                                        </p>
                                      ))}
                                    </div>
                                  )}
                                </div>
                                <span className="shrink-0 text-xs" style={{ color: 'var(--text-3)' }}>×{item.quantity} · {item.total_price.toLocaleString('ru-RU')} ₽</span>
                              </div>
                            );
                          })}
                        </div>
                        {order.discount_pct > 0 && (
                          <div className="mt-2 flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded-lg"
                            style={{ background: 'rgba(99,179,237,0.1)', color: 'var(--cyan)', border: '1px solid var(--border-glass)' }}>
                            <Star size={10} fill="currentColor" /> Скидка {order.discount_pct.toFixed(0)}% → {order.final_amount.toLocaleString('ru-RU')} ₽
                          </div>
                        )}
                        {crmUi && (
                          <p className="text-[10px] mt-2 px-1 leading-relaxed" style={{ color: 'var(--text-3)' }}>
                            Редактирование позиций и скидки — в «Карточке сделки».
                          </p>
                        )}
                      </div>

                      {order.notes && (
                        <div className="rounded-xl px-3 py-2 text-xs"
                          style={{ background: 'rgba(246,173,85,0.1)', color: '#F6AD55', border: '1px solid rgba(246,173,85,0.25)' }}>
                          💬 {order.notes}
                        </div>
                      )}

                      {isProductionSandbox && isExpanded && (
                        <AdminGlassDisclosure
                          accent="emerald"
                          defaultOpen
                          density="compact"
                          title="Заметка для цеха"
                          subtitle="Краткая сводка в карточке заказа"
                          icon={<Factory size={15} color="#68D391" />}
                        >
                          <p className="text-[10px] leading-relaxed" style={{ color: 'var(--text-3)' }}>
                            Полная лента и handoff — во вкладке «Воронка» → карточка сделки, блок «Лента».
                          </p>
                        </AdminGlassDisclosure>
                      )}

                      {fullAdmin && (
                      <AdminGlassDisclosure
                        accent="violet"
                        defaultOpen={false}
                        density="compact"
                        title="Комментарий менеджера"
                        subtitle="Уходит клиенту вместе со статусом"
                        icon={<MessageCircle size={15} color="#D6BCFA" />}
                      >
                        <textarea
                          rows={2}
                          placeholder="Отправится клиенту вместе со статусом"
                          value={notes[order.id] ?? (order.manager_notes || '')}
                          onChange={e => setNotes(prev => ({ ...prev, [order.id]: e.target.value }))}
                          className="input-glass w-full text-sm rounded-xl px-3 py-2 resize-none"
                        />
                        <div className="flex gap-1.5 overflow-x-auto no-scrollbar mt-2 pb-1">
                          {ORDER_NOTE_TEMPLATES.map((tpl) => (
                            <button
                              key={tpl}
                              type="button"
                              onClick={() => setNotes(prev => ({ ...prev, [order.id]: tpl }))}
                              className="press shrink-0 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold"
                              style={{ background: 'rgba(99,179,237,0.1)', color: 'var(--cyan)', border: '1px solid var(--border-glass)' }}
                            >
                              {tpl}
                            </button>
                          ))}
                        </div>
                        <button
                          type="button"
                          onClick={() => { haptic.selection(); saveManagerNote(order.id, order.status); }}
                          disabled={updating === order.id}
                          className="press mt-2 w-full py-2 rounded-xl text-xs font-bold disabled:opacity-50"
                          style={{ background: 'rgba(99,179,237,0.2)', color: 'var(--cyan-bright)', border: '1px solid var(--cyan-border)' }}
                        >
                          {updating === order.id ? 'Сохраняем...' : 'Сохранить комментарий'}
                        </button>
                      </AdminGlassDisclosure>
                      )}

                      {fullAdmin && (
                      <AdminGlassDisclosure
                        accent="coral"
                        defaultOpen={false}
                        density="compact"
                        title="Скидка заказа"
                        subtitle="Процент и пересчёт итога"
                        icon={<Star size={15} color="#FC814A" />}
                      >
                        <div className="flex gap-2">
                          <input
                            type="number"
                            min={0}
                            max={100}
                            step="0.1"
                            value={orderDiscounts[order.id] ?? order.discount_pct}
                            onChange={e => setOrderDiscounts(prev => ({ ...prev, [order.id]: Number(e.target.value || 0) }))}
                            className="input-glass flex-1 px-3 py-2 rounded-xl text-sm"
                          />
                          <button
                            type="button"
                            onClick={() => { haptic.selection(); updateOrderPricing(order.id); }}
                            disabled={updating === order.id}
                            className="press px-3 py-2 rounded-xl text-xs font-bold disabled:opacity-50"
                            style={{ background: 'rgba(72,187,120,0.2)', color: '#68D391', border: '1px solid rgba(72,187,120,0.4)' }}
                          >
                            Применить
                          </button>
                        </div>
                      </AdminGlassDisclosure>
                      )}

                      {orderStatusEditor && (
                      <AdminGlassDisclosure
                        accent="cyan"
                        density="compact"
                        title="Статус для клиента"
                        subtitle="Как раньше: кнопка → лист → Telegram"
                        icon={<RefreshCw size={14} color="#63B3ED" />}
                        defaultOpen
                      >
                        {crmUi && (
                          <p className="text-[10px] mb-2 px-1" style={{ color: 'var(--text-3)' }}>
                            Этапы воронки (замер, состав) — в{' '}
                            <button
                              type="button"
                              className="press font-bold underline touch-manipulation"
                              style={{ color: '#63B3ED' }}
                              onClick={() => {
                                haptic.selection();
                                openDealCard(order.id);
                              }}
                            >
                              карточке CRM
                            </button>
                            .
                          </p>
                        )}
                        <CrmOrderStatusPanel
                          orderId={order.id}
                          orderNumber={order.order_number}
                          currentStatus={order.status}
                          hasTelegram={Boolean(order.user?.telegram_id)}
                          disabled={updating === order.id}
                          onUpdated={onOrderStatusUpdated}
                          hapticSelection={() => haptic.selection()}
                          hapticSuccess={() => haptic.impact('light')}
                        />
                      </AdminGlassDisclosure>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
    </>
  );
}
